import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Palette, Layout, Type, ArrowRight } from "lucide-react";

export const PersonalizacionCard = () => {
  const navigate = useNavigate();

  return (
    <div className="relative group bg-gradient-to-br from-primary/10 via-primary/5 to-background border-2 border-primary/30 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
      {/* Badge destacado */}
      <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10">
        <Badge className="bg-gradient-to-r from-primary to-primary-dark text-brown-dark font-bold px-2 py-1 sm:px-3 sm:py-1.5 shadow-md text-xs sm:text-sm">
          ⭐ Más Popular
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6 md:p-8">
        {/* Lado izquierdo - Visual */}
        <div className="relative aspect-[4/3] sm:aspect-[16/9] md:aspect-auto md:min-h-[400px] bg-gradient-to-br from-primary/20 via-primary/10 to-peach/20 rounded-xl overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center p-4 sm:p-6 md:p-8">
            <div className="relative w-full h-full max-w-md mx-auto">
              {/* Mockup de agenda personalizable */}
              <div className="absolute inset-0 bg-white/90 dark:bg-gray-800/90 rounded-lg shadow-2xl p-3 sm:p-4 md:p-6 transform rotate-[-2deg] group-hover:rotate-0 transition-transform duration-300">
                <div className="w-full h-full border-2 border-dashed border-primary/40 rounded flex flex-col items-center justify-center gap-2 sm:gap-3 md:gap-4 text-center">
                  <Sparkles className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 text-primary animate-pulse" />
                  <div>
                    <p className="font-playfair font-bold text-lg sm:text-xl md:text-2xl text-brown-dark mb-1">
                      Tu diseño único
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      100% personalizado a tu estilo
                    </p>
                  </div>
                </div>
              </div>

              {/* Elementos flotantes decorativos */}
              <div className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-primary/20 rounded-full p-2 sm:p-3 animate-bounce">
                <Palette className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-primary" />
              </div>
              <div className="absolute bottom-3 left-2 sm:bottom-6 sm:left-4 bg-primary/20 rounded-full p-2 sm:p-3 animate-pulse">
                <Type className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-primary" />
              </div>
            </div>
          </div>

          {/* Overlay gradient en hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>

        {/* Lado derecho - Contenido */}
        <div className="flex flex-col justify-center space-y-4 sm:space-y-5 md:space-y-6">
          <div className="space-y-2 sm:space-y-3">
            <h3 className="font-playfair font-bold text-2xl sm:text-3xl md:text-4xl text-brown-dark leading-tight">
              CEO Planner Personalizado
            </h3>

            <p className="text-sm sm:text-base md:text-lg text-brown-light/90 leading-relaxed">
              Diseña tu agenda desde cero con nuestro sistema guiado paso a
              paso. Elige portada, hojas internas, colores y detalles únicos que
              reflejen tu estilo.
            </p>
          </div>

          {/* Features en grid 2 columnas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            <div className="flex items-start gap-2 sm:gap-3 bg-white/50 dark:bg-gray-800/50 p-2.5 sm:p-3 rounded-lg">
              <Layout className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-brown-dark">
                  Formato flexible
                </p>
                <p className="text-xs text-muted-foreground">
                  Elige tamaño y secciones
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 bg-white/50 dark:bg-gray-800/50 p-2.5 sm:p-3 rounded-lg">
              <Palette className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-brown-dark">
                  Diseño único
                </p>
                <p className="text-xs text-muted-foreground">
                  Portada y colores a tu gusto
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 bg-white/50 dark:bg-gray-800/50 p-2.5 sm:p-3 rounded-lg">
              <Type className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-brown-dark">
                  Personalización total
                </p>
                <p className="text-xs text-muted-foreground">
                  Agrega tu nombre o frase
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:gap-3 bg-white/50 dark:bg-gray-800/50 p-2.5 sm:p-3 rounded-lg">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-brown-dark">
                  Hecha para ti
                </p>
                <p className="text-xs text-muted-foreground">
                  Producción personalizada
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="space-y-2">
            <Button
              size="lg"
              onClick={() => navigate("/personaliza-agenda")}
              className="w-full sm:w-auto bg-gradient-to-r from-primary via-primary-dark to-primary text-brown-dark hover:shadow-xl hover:scale-[1.02] font-bold px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 rounded-full text-sm sm:text-base md:text-lg transition-all duration-300 group/btn relative overflow-hidden"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-primary-dark via-primary to-primary-dark opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></span>
              <span className="relative z-10 flex items-center justify-center gap-1.5 sm:gap-2">
                <span className="hidden xs:inline">Crear Mi CEO Planner Ahora · </span>
                <span className="xs:hidden">Crear Ahora · </span>
                $35.00
                <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 group-hover/btn:translate-x-1 transition-transform" />
              </span>
            </Button>

            {/* Microcopy tranquilizador */}
            <p className="text-xs text-muted-foreground italic text-center sm:text-left">
              ✨ Personalización guiada paso a paso · Sin costo adicional por
              diseño
            </p>
          </div>
        </div>
      </div>

      {/* Borde animado en hover */}
      <div className="absolute inset-0 rounded-2xl border-2 border-primary/0 group-hover:border-primary/50 transition-colors duration-300 pointer-events-none" />
    </div>
  );
};
